import { Button, HStack, VStack } from '@chakra-ui/react';
import ArrowForwardSharpIcon from '@gamma/ui/icons/ArrowForwardSharpIcon';
import { RouterLink } from '@gamma/ui/navigation';
import { useContext, useMemo } from 'react';
import { ROUTES } from '@investigator/shared/constants';
import { useQueryParams } from '@investigator/shared/hooks/useQueryParams';
import { i18n } from '@investigator/shared/localization';
import { StatusStrings } from '../../../../../entities/components';
import {
  GET_DETECTIONS,
  QUERY_DETECTIONS_PAGINATED,
} from '../../../../queries';
import type { SantizedDetection } from '../../../../pages/Detections';
import { AuthContext } from '@investigator/shared/providers/AuthProvider';
import { DetectionQueryVariables } from '../../../../pages';
import { ActionsMenu, ActionsMenuButton } from '../../../DetectionActions';
import { getSourceEntityForSuppression } from '@investigator/shared/utils';
import { Detection } from '@investigator/shared/generated/graphql';
import { useDetectionEntityStatusBanners } from '../../../../hooks';

const { viewDetection } = i18n.pages.detections;

export interface DetectionDrawerActionsProps {
  detection?: Detection | SantizedDetection;
  isRelated?: boolean;
  isRowActions?: boolean;
  isRowActionsExpanded?: boolean;
  isDetailsPage?: boolean;
  setIsSlideDrawerOpen?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >;
  relatedEntitiesVariables?: DetectionQueryVariables;
  relatedDetectionsVariables?: DetectionQueryVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
}

export const DetectionDrawerActions = ({
  detection,
  isRelated,
  isRowActions,
  isRowActionsExpanded,
  relatedEntitiesVariables,
  relatedDetectionsVariables,
  queryDetectionsPaginatedVariables,
}: DetectionDrawerActionsProps) => {
  const { userRole } = useContext(AuthContext);

  const { detection_id, tenant_info } = detection || {};

  const getDetectionVariables = {
    detection_ids: [
      {
        detection_id,
        tenant: tenant_info?.tenant_id ?? undefined,
      },
    ],
  };

  const sourceEntity = useMemo(
    () => (detection ? getSourceEntityForSuppression(detection) : undefined),
    [detection],
  );

  // Use shared hook to get status strings (suppression & escalation banners)
  const { statusStrings, excludedEntitiesData, excludedEntitiesLoading } =
    useDetectionEntityStatusBanners({
      detection,
      sourceEntity,
    });

  const refetchQueries = [
    ...(getDetectionVariables
      ? [
          {
            query: GET_DETECTIONS,
            variables: getDetectionVariables,
          },
        ]
      : []),
    ...(relatedEntitiesVariables
      ? [
          {
            query: QUERY_DETECTIONS_PAGINATED,
            variables: relatedEntitiesVariables,
          },
        ]
      : []),
    ...(relatedDetectionsVariables
      ? [
          {
            query: QUERY_DETECTIONS_PAGINATED,
            variables: relatedDetectionsVariables,
          },
        ]
      : []),
    ...(queryDetectionsPaginatedVariables
      ? [
          {
            query: QUERY_DETECTIONS_PAGINATED,
            variables: queryDetectionsPaginatedVariables,
          },
        ]
      : []),
  ];

  const searchParams = useQueryParams();
  const detailsTo = useMemo(() => {
    const searchDetectionId = searchParams.get('detection_id');
    if (searchDetectionId) {
      searchParams.delete('detection_id');
    }
    if (detection) {
      return {
        pathName: `${ROUTES.detections}/${detection.detection_id}/details`,
        search: searchParams.toString(),
      };
    }
    return undefined;
  }, [searchParams, detection]);

  if (!detection) {
    return null;
  }

  return (
    <VStack w="100%" spacing={4}>
      <HStack w="100%" justifyContent="space-between">
        {!isRowActions ? (
          <ActionsMenuButton
            detection={undefined} // Don't pass detection in drawer, let it read from context
            isRelated={isRelated}
            isRowActions={isRowActions}
            refetchQueries={refetchQueries}
            excludedEntitiesData={excludedEntitiesData}
            excludedEntitiesLoading={excludedEntitiesLoading}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
            userRole={userRole}
          />
        ) : (
          <ActionsMenu
            detection={detection} // Pass detection for row actions
            isRelated={isRelated}
            isRowActions={isRowActions}
            isRowActionsExpanded={isRowActionsExpanded}
            refetchQueries={refetchQueries}
            excludedEntitiesData={excludedEntitiesData}
            excludedEntitiesLoading={excludedEntitiesLoading}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
            userRole={userRole}
          />
        )}
        {!isRowActions && (
          <Button
            size="sm"
            as={RouterLink}
            variant="solid"
            colorScheme="blue"
            rightIcon={<ArrowForwardSharpIcon />}
            to={`${detailsTo?.pathName}?${detailsTo?.search}`}
            data-testid="view-detection-button"
          >
            {viewDetection}
          </Button>
        )}
      </HStack>
      {!isRowActions && statusStrings?.length > 0 && (
        <StatusStrings statusStrings={statusStrings} />
      )}
    </VStack>
  );
};
